import React, { useState, useEffect, useCallback, useRef } from 'react';
import JSZip from 'jszip';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Alert,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  Pagination,
  Card,
  CardContent,
  styled
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandLess as ExpandLessIcon,
  Book as BookIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { billingApi } from '../../api/billingApi';
import backgroundPattern from '../../assets/pasbookpng.png';
import '../../style/BillingStatements.css';

const AnimatedRefreshIcon = ({ size = 18 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <g>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
      <animateTransform
        attributeName="transform"
        attributeType="XML"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="1s"
        repeatCount="1"
        fill="freeze" />
    </g>
  </svg>
);

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#E2E8F0',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: '#94A3B8',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4338CA',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748B',
    fontWeight: 500,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#4338CA',
  },
}));

const BillingStatements = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [billingData, setBillingData] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [summary, setSummary] = useState({
    totalCustomers: 0,
    withEntries: 0,
    noEntries: 0
  });
  const [bulkProgress, setBulkProgress] = useState({
    isOpen: false,
    current: 0,
    total: 0,
    status: '',
    completed: 0,
    failed: 0
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterExpanded, setFilterExpanded] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [backgroundSize, setBackgroundSize] = useState('220px');
  const hasInitialFetch = useRef(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCustomerForView, setSelectedCustomerForView] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);

  // Responsive background size
  useEffect(() => {
    const updateBackgroundSize = () => {
      if (window.innerWidth <= 480) {
        setBackgroundSize('120px');
      } else if (window.innerWidth <= 768) {
        setBackgroundSize('150px');
      } else {
        setBackgroundSize('220px');
      }
    };

    updateBackgroundSize();
    window.addEventListener('resize', updateBackgroundSize);
    return () => window.removeEventListener('resize', updateBackgroundSize);
  }, []);

  // Generate month options (last 12 months + current)
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i <= 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = generateMonthOptions();

  // Calculate date range from selected month
  const getMonthDateRange = useCallback((monthStr) => {
    // Parse month string (format: 2026-08)
    const [year, month] = monthStr.split('-').map(Number);
    
    // Create dates using local time to avoid timezone issues
    const firstDay = new Date(year, month - 1, 1); // month is 0-indexed in Date constructor
    const lastDay = new Date(year, month, 0); // month + 1 is next month, day 0 is last day of previous month
    
    // Format as YYYY-MM-DD using local time
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    const fromDate = formatDate(firstDay);
    const toDate = formatDate(lastDay);
    
    console.log(`Month: ${monthStr}, Year: ${year}, First day: ${fromDate}, Last day: ${toDate}`);
    
    return { fromDate, toDate };
  }, []);

  // Fetch monthly billing status
  const fetchBillingStatus = useCallback(async (month = selectedMonth, customer = selectedCustomer) => {
    setLoading(true);
    setError(null);
    try {
      const data = await billingApi.getMonthlyStatus(month, customer);
      setBillingData(data);
      
      // Calculate summary
      const total = data.length;
      const withEntries = data.filter(item => item.hasEntry).length;
      const noEntries = total - withEntries;
      
      setSummary({
        totalCustomers: total,
        withEntries,
        noEntries
      });
      
      // Clear selected customers when data changes
      setSelectedCustomers([]);
    } catch (err) {
      setError('Failed to load billing status. Please try again.');
      console.error('Error fetching billing status:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedCustomer]);

  useEffect(() => {
    const selectedCustomerId = location.state?.selectedCustomerId;
    if (!selectedCustomerId) return;

    setSelectedCustomer(String(selectedCustomerId));
    setCurrentPage(0);
    fetchBillingStatus(selectedMonth, String(selectedCustomerId));
  }, [location.state, selectedMonth, fetchBillingStatus]);

  // Initial load - only run once
  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchBillingStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle customer selection
  const handleCustomerSelect = (customerId) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    const customersWithEntries = billingData.filter(item => item.hasEntry).map(item => item.customerId);
    if (selectedCustomers.length === customersWithEntries.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customersWithEntries);
    }
  };

  // Handle single PDF download using existing API
  const handleDownloadPdf = async (customerId, customerName) => {
    try {
      const { fromDate, toDate } = getMonthDateRange(selectedMonth);
      const blob = await billingApi.generateStatementPdf(customerId, fromDate, toDate);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Format filename: CustomerName-Month-Year-Statement.pdf
      const monthObj = new Date(selectedMonth + '-01');
      const monthName = monthObj.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const safeName = customerName.replace(/\s+/g, '-');
      a.download = `${safeName}-${monthName}-Statement.pdf`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download PDF. Please try again.');
      console.error('Error downloading PDF:', err);
    }
  };

  // Handle bulk PDF download with progress
  const handleBulkDownload = async () => {
    if (selectedCustomers.length === 0) {
      setError('Please select at least one customer.');
      return;
    }

    const customersWithEntries = selectedCustomers
      .map(id => billingData.find(c => c.customerId === id))
      .filter(c => c && c.hasEntry);

    if (customersWithEntries.length === 0) {
      setError('No customers with entries selected.');
      return;
    }

    setBulkProgress({
      isOpen: true,
      current: 0,
      total: customersWithEntries.length,
      status: 'Generating statements...',
      completed: 0,
      failed: 0
    });

    const zip = new JSZip();
    const failedCustomers = [];

    try {
      for (let i = 0; i < customersWithEntries.length; i++) {
        const customer = customersWithEntries[i];
        setBulkProgress(prev => ({
          ...prev,
          current: i + 1,
          status: `Generating statement for ${customer.customerName}...`
        }));

        try {
          const { fromDate, toDate } = getMonthDateRange(selectedMonth);
          const blob = await billingApi.generateStatementPdf(customer.customerId, fromDate, toDate);
          
          // Format filename: CustomerName_Month_Year.pdf
          const monthObj = new Date(selectedMonth + '-01');
          const monthName = monthObj.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          const safeName = customer.customerName.replace(/\s+/g, '_');
          const filename = `${safeName}_${monthName.replace(/\s+/g, '_')}.pdf`;
          
          zip.file(filename, blob);
          
          setBulkProgress(prev => ({
            ...prev,
            completed: prev.completed + 1
          }));
        } catch (err) {
          console.error(`Failed to generate PDF for ${customer.customerName}:`, err);
          failedCustomers.push(customer.name);
          setBulkProgress(prev => ({
            ...prev,
            failed: prev.failed + 1
          }));
        }
      }

      // Generate ZIP file with month name
      setBulkProgress(prev => ({
        ...prev,
        status: 'Creating ZIP file...'
      }));

      const monthObj = new Date(selectedMonth + '-01');
      const monthName = monthObj.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const zipFileName = `Billing_${monthName.replace(/\s+/g, '_')}.zip`;

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Download ZIP file
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setBulkProgress(prev => ({
        ...prev,
        status: 'Download complete!'
      }));

    } catch (err) {
      setError('Failed to download bulk PDF. Please try again.');
      console.error('Error downloading bulk PDF:', err);
    }
  };

  // Handle download all PDFs
  const handleDownloadAll = async () => {
    const customersWithEntries = billingData
      .filter(item => item.hasEntry)
      .map(item => item.customerId);
    
    if (customersWithEntries.length === 0) {
      setError('No customers with entries to download.');
      return;
    }

    setSelectedCustomers(customersWithEntries);
    await handleBulkDownload();
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage - 1);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  // Calculate paginated data
  const paginatedData = billingData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const totalPages = Math.ceil(billingData.length / rowsPerPage);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchBillingStatus(selectedMonth, selectedCustomer);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Handle view statement
  const handleViewStatement = (customerId) => {
    // Use existing getMonthDateRange function for correct date calculation
    const { fromDate, toDate } = getMonthDateRange(selectedMonth);
    
    console.log("View statement for customer:", customerId, "from:", fromDate, "to:", toDate);
    
    // Navigate to passbook with date filters using React Router
    navigate(`/dashboard/customers/passbook?customerId=${customerId}&fromDate=${fromDate}&toDate=${toDate}`);
  };

  // Handle view menu click
  const handleViewMenuClick = (event, customer) => {
    event.stopPropagation();
    setSelectedCustomerForView(customer);
    setViewDialogOpen(true);
  };

  // Handle view menu close
  const handleViewMenuClose = () => {
    setViewDialogOpen(false);
    setSelectedCustomerForView(null);
  };

  // Handle view passbook click
  const handleViewPassbookClick = () => {
    if (selectedCustomerForView) {
      handleViewStatement(selectedCustomerForView.customerId);
    }
    handleViewMenuClose();
  };

  // Handle download statement click - opens preview instead of direct download
  const handleDownloadStatementClick = () => {
    if (selectedCustomerForView) {
      handlePdfPreview(selectedCustomerForView.customerId, selectedCustomerForView.customerName);
    }
    handleViewMenuClose();
  };

  // Handle PDF preview
  const handlePdfPreview = async (customerId, customerName) => {
    setPdfPreviewLoading(true);
    setPdfPreviewOpen(true);
    try {
      const { fromDate, toDate } = getMonthDateRange(selectedMonth);
      const blob = await billingApi.generateStatementPdf(customerId, fromDate, toDate);
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
    } catch (err) {
      setError('Failed to load PDF preview. Please try again.');
      console.error('Error loading PDF preview:', err);
      setPdfPreviewOpen(false);
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  // Handle PDF preview close
  const handlePdfPreviewClose = () => {
    setPdfPreviewOpen(false);
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  // Handle download from preview
  const handleDownloadFromPreview = () => {
    if (pdfPreviewUrl && selectedCustomerForView) {
      const a = document.createElement('a');
      a.href = pdfPreviewUrl;
      
      const monthObj = new Date(selectedMonth + '-01');
      const monthName = monthObj.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const safeName = selectedCustomerForView.customerName.replace(/\s+/g, '-');
      a.download = `${safeName}-${monthName}-Statement.pdf`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="billing-statements-page" style={{ position: 'relative', padding: '8px 4px 36px', minHeight: '100vh', background: '#f6f8fb' }}>
      <div
        style={{
          position: 'fixed',
          inset: '-30%',
          backgroundImage: `url(${backgroundPattern})`,
          backgroundRepeat: 'repeat',
          backgroundSize: backgroundSize,
          transform: 'rotate(-12deg)',
          opacity: 0.08,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: '#4338CA',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 8px 20px rgba(67,56,202,.18)',
            }}
          >
            <AssignmentIcon sx={{ fontSize: 25 }} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: { xs: '24px', md: '30px' },
                fontWeight: 700,
                lineHeight: 1.15,
                color: '#0F172A',
              }}
            >
              Billing / Statements
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: '14px',
                color: '#64748B',
              }}
            >
              Monthly customer billing and statement management
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          startIcon={isRefreshing ? <AnimatedRefreshIcon size={18} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading || isRefreshing}
          sx={{
            height: 46,
            minWidth: 120,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      {!filterExpanded ? (
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={() => setFilterExpanded(true)}
          sx={{
            mb: 3,
            height: 48,
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "14px",
            borderColor: "#E5E7EB",
            color: "#64748b",
            "&:hover": {
              borderColor: "#2563eb",
              background: "#eff6ff",
              color: "#2563eb",
            },
          }}
        >
          Show Filters
        </Button>
      ) : (
        <Card
          sx={{
            mb: 3,
            borderRadius: "22px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 10px 30px rgba(15,23,42,.05)",
            overflow: "visible",
            background: "#fff"
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              "&:last-child": {
                pb: { xs: 2, sm: 2.5, md: 3 }
              }
            }}
          >
            {/* Header row */}
            <Box sx={{
              display: "flex",
              alignItems: { xs: "center", sm: "flex-start" },
              justifyContent: "space-between",
              mb: { xs: 2, sm: 2.5 },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 }
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }} onClick={() => setFilterExpanded(false)}>
                <Box sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#eff6ff",
                  color: "#2563eb",
                }}>
                  <SearchIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                  Filters
                </Typography>
                <ExpandLessIcon sx={{ fontSize: 20, color: "#64748b" }} />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    setSelectedCustomer('all');
                    setSelectedMonth(() => {
                      const now = new Date();
                      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    });
                    fetchBillingStatus();
                  }}
                  sx={{
                    height: 36,
                    mt: { xs: 0, sm: 0.5 },
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    textTransform: "none",
                    color: "#64748b",
                    "&:hover": {
                      background: "#f1f5f9",
                      color: "#334155",
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>

            {/* Fields row */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(2, 1fr)",
              },
              alignItems: "flex-end",
              gap: { xs: 2, sm: 2, md: 2 }
            }}>
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#64748B", mb: 0.5 }}>
                  Customer
                </Typography>
                <StyledFormControl fullWidth size="small">
                  <Select
                    value={selectedCustomer}
                    onChange={(e) => {
                      setSelectedCustomer(e.target.value);
                      fetchBillingStatus(selectedMonth, e.target.value);
                    }}
                    sx={{
                      height: 40,
                      borderRadius: '10px',
                      background: '#fff',
                    }}
                  >
                    <MenuItem value="all">All Customers</MenuItem>
                    {billingData.map((customer) => (
                      <MenuItem key={customer.customerId} value={customer.customerId}>
                        {customer.customerName}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#64748B", mb: 0.5 }}>
                  Billing Month
                </Typography>
                <StyledFormControl fullWidth size="small">
                  <Select
                    value={selectedMonth}
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                      fetchBillingStatus(e.target.value, selectedCustomer);
                    }}
                    sx={{
                      height: 40,
                      borderRadius: '10px',
                      background: '#fff',
                    }}
                  >
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

            {/* Summary Cards */}
      <Box className="bs-summary-grid" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        {[
          { label: 'Total Customers', value: summary.totalCustomers, icon: <PersonIcon />, color: '#818CF8', bg: '#fff' },
          { label: 'With Entries', value: summary.withEntries, icon: <CheckCircleIcon />, color: '#34D399', bg: '#fff' },
          { label: 'No Entries', value: summary.noEntries, icon: <CancelIcon />, color: '#FBBF24', bg: '#fff' },
        ].map((item) => (
          <Box key={item.label} className="bs-summary-card" sx={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '14px', p: 2, display: 'flex', alignItems: 'center', gap: 1.5, boxShadow: '0 2px 8px rgba(15,23,42,.04)' }}>
            <Box className="bs-summary-icon-box" sx={{ width: 42, height: 42, borderRadius: '10px', background: item.bg, color: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {React.cloneElement(item.icon, { sx: { fontSize: 21 } })}
            </Box>
            <Box className="bs-summary-content-box" sx={{ background: 'transparent' }}>
              <Typography sx={{ fontSize: 12, color: '#64748B', fontWeight: 600, background: 'transparent' }}>{item.label}</Typography>
              <Typography sx={{ mt: 0.25, fontSize: 22, fontWeight: 700, color: '#0F172A', background: 'transparent' }}>{item.value}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Table Section */}
      <Box
        className="bs-table-card"
        sx={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '16px',
          boxShadow: '0 4px 14px rgba(15,23,42,.05)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 1.75,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: '#0F172A',
              }}
            >
              Billing Statements
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                color: '#64748B',
                mt: 0.25,
              }}
            >
              Customer statement availability
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleBulkDownload}
              disabled={selectedCustomers.length === 0 || loading}
              sx={{
                height: 38,
                borderRadius: '9px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Download Selected
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadAll}
              disabled={summary.withEntries === 0 || loading}
              sx={{
                height: 38,
                borderRadius: '9px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Download All
            </Button>
          </Box>
        </Box>

        <TableContainer
          sx={{
            width: '100%',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 7,
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E1',
              borderRadius: 10,
            },
          }}
        >
          <Table
            sx={{
              minWidth: 760,
              tableLayout: 'auto',

              '& th': {
                background: '#F8FAFC',
                color: '#475569',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.5px',
                whiteSpace: 'nowrap',
                borderBottom: '1px solid #E5E7EB',
              },

              '& th:last-child': {
                textAlign: 'center !important',
              },

              '& td': {
                fontSize: 13,
                color: '#334155',
                borderBottom: '1px solid #F1F5F9',
              },

              '& td:last-child': {
                textAlign: 'center !important',
              },

              '& tbody tr:hover': {
                background: '#F8FBFF',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedCustomers.length > 0 &&
                      selectedCustomers.length < summary.withEntries
                    }
                    checked={
                      summary.withEntries > 0 &&
                      selectedCustomers.length === summary.withEntries
                    }
                    onChange={handleSelectAll}
                    disabled={summary.withEntries === 0}
                    size="small"
                  />
                </TableCell>

                <TableCell>Customer</TableCell>

                <TableCell align="center">
                  Challans
                </TableCell>

                <TableCell align="center">
                  Status
                </TableCell>

                <TableCell sx={{ textAlign: 'center', '&.MuiTableCell-root': { textAlign: 'center' } }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.map((customer) => (
                <TableRow
                  key={customer.customerId}
                  hover
                  sx={{
                    height: 58,
                    '& td': {
                      py: 1.25,
                      px: 1.5,
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedCustomers.includes(customer.customerId)}
                      onChange={() =>
                        handleCustomerSelect(customer.customerId)
                      }
                      disabled={!customer.hasEntry}
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#2563EB',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 220,
                      }}
                    >
                      {customer.customerName}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#334155',
                      }}
                    >
                      {customer.challanCount}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      label={
                        customer.hasEntry
                          ? 'AVAILABLE'
                          : 'NO ENTRY'
                      }
                      size="small"
                      sx={{
                        height: 28,
                        borderRadius: '14px',
                        fontSize: 10,
                        fontWeight: 700,
                        background: customer.hasEntry
                          ? '#DCFCE7'
                          : '#F1F5F9',
                        color: customer.hasEntry
                          ? '#15803D'
                          : '#64748B',
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    {customer.hasEntry ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 0.75,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            handleViewMenuClick(e, customer)
                          }
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            color: '#2563EB',
                            background: '#EFF6FF',
                            '&:hover': {
                              background: '#DBEAFE',
                            },
                          }}
                        >
                          <ViewIcon sx={{ fontSize: 17 }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDownloadPdf(
                              customer.customerId,
                              customer.customerName
                            )
                          }
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            color: '#059669',
                            background: '#ECFDF5',
                            '&:hover': {
                              background: '#D1FAE5',
                            },
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          color: '#94A3B8',
                          fontSize: 13,
                        }}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {billingData.length > 0 && (
          <Box
            sx={{
              px: { xs: 2, md: 2.5 },
              py: 2,
              borderTop: '1px solid #E5E7EB',
            }}
          >
            <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', mb: 2 }}>
              Showing {currentPage * rowsPerPage + 1}–{Math.min((currentPage + 1) * rowsPerPage, billingData.length)} of {billingData.length} records
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage + 1}
                onChange={handlePageChange}
                disabled={loading}
                shape="rounded"
                variant="outlined"
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={0}
                boundaryCount={1}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    transition: "transform .2s, box-shadow .2s, background-color .2s",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 8px rgba(67,56,202,.15)",
                    },
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  sx={{
                    height: 36,
                    borderRadius: '8px',
                    fontSize: '13px',
                  }}
                >
                  <MenuItem value={5}>5 per page</MenuItem>
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={25}>25 per page</MenuItem>
                  <MenuItem value={50}>50 per page</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
      </Box>

      {/* Bulk Download Progress Dialog */}
      <Dialog open={bulkProgress.isOpen} onClose={() => setBulkProgress({ ...bulkProgress, isOpen: false })} maxWidth="sm" fullWidth>
        <DialogContent sx={{ position: 'relative' }}>
          <IconButton 
            onClick={() => setBulkProgress({ ...bulkProgress, isOpen: false })}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ mt: 2, pr: 4 }}>
            <Typography variant="h6" gutterBottom>
              Generating Statements
            </Typography>
            <Typography variant="body2" gutterBottom>
              {bulkProgress.status}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(bulkProgress.current / bulkProgress.total) * 100} 
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary">
              {bulkProgress.current} / {bulkProgress.total} completed
            </Typography>
            {bulkProgress.completed > 0 && (
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                ✓ {bulkProgress.completed} Generated
              </Typography>
            )}
            {bulkProgress.failed > 0 && (
              <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                ✕ {bulkProgress.failed} Failed
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkProgress({ ...bulkProgress, isOpen: false })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Options Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleViewMenuClose}
        sx={{ zIndex: 1300 }}
        PaperProps={{
          sx: {
            width: '95vw',
            maxWidth: 720,
            maxHeight: '90vh',
            margin: '16px',
            borderRadius: 18,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '18px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <ViewIcon sx={{ fontSize: 24, color: '#4338ca' }} />
                View Options
              </Typography>
              {selectedCustomerForView && (
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '14px', mt: 0.5 }}>
                  {selectedCustomerForView.customerName}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleViewMenuClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              fullWidth
              onClick={handleViewPassbookClick}
              sx={{
                height: 48,
                borderRadius: 12,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                justifyContent: 'flex-start',
                px: 2,
                color: '#1e293b',
                background: '#F8FAFC',
                '&:hover': {
                  background: '#F1F5F9',
                }
              }}
            >
              <BookIcon sx={{ fontSize: 20, mr: 2, color: '#4338ca' }} />
              View Passbook
            </Button>
            <Button
              fullWidth
              onClick={handleDownloadStatementClick}
              sx={{
                height: 48,
                borderRadius: 12,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                justifyContent: 'flex-start',
                px: 2,
                color: '#1e293b',
                background: '#F8FAFC',
                '&:hover': {
                  background: '#F1F5F9',
                }
              }}
            >
              <DescriptionIcon sx={{ fontSize: 20, mr: 2, color: '#059669' }} />
              Download Statement PDF
            </Button>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={handleViewMenuClose}
            sx={{
              height: 46,
              minWidth: 110,
              borderRadius: 12,
              textTransform: 'none',
              fontWeight: 600,
              background: '#F8FAFC',
              border: '1px solid #CBD5E1',
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={handlePdfPreviewClose}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            borderRadius: 16,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '18px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon sx={{ fontSize: 24, color: '#059669' }} />
                PDF Preview
              </Typography>
              {selectedCustomerForView && (
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '14px', mt: 0.5 }}>
                  {selectedCustomerForView.customerName} - {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handlePdfPreviewClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0, height: 'calc(90vh - 140px)', overflow: 'hidden' }}>
          {pdfPreviewLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <LinearProgress sx={{ width: '60%', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">Loading PDF preview...</Typography>
            </Box>
          ) : pdfPreviewUrl ? (
            <iframe
              src={pdfPreviewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="PDF Preview"
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="error">Failed to load PDF preview</Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={handlePdfPreviewClose}
            sx={{
              height: 46,
              minWidth: 110,
              borderRadius: 12,
              textTransform: 'none',
              fontWeight: 600,
              background: '#F8FAFC',
              border: '1px solid #CBD5E1',
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownloadFromPreview}
            disabled={!pdfPreviewUrl}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              height: 46,
              minWidth: 140,
              borderRadius: 12,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </div>
  );
};

export default BillingStatements;